import { useTranslation } from 'react-i18next';
import { FileText, Loader2, X } from '@parrot-software/design-system/icons';
import { cn } from '@parrot-software/utils';

interface CsfFileCardProps {
  className?: string;
  /** Error message to display below the file card. */
  error?: string | null;
  /** The file being displayed. */
  file: File;
  /** Whether extraction is in progress. */
  isExtracting?: boolean;
  /** Called when the remove button is clicked. Omit to hide the button. */
  onRemove?: () => void;
  /** Controls when the remove button is visible. Defaults to 'always'. */
  showRemoveButton?: 'always' | 'on-error' | 'never';
}

/**
 * Displays a selected CSF file with its name, optional remove button, extraction spinner, and error message.
 *
 * Extracted from CsfUploadSection and InvoiceDataProofUploadStep to reduce duplication.
 * The card shows a file icon, the filename truncated to fit, and a remove button when applicable.
 * Below the card, a spinner appears during extraction; an error message appears on failure.
 *
 * @param {CsfFileCardProps} props - Component properties
 * @returns {React.ReactElement} CSF file card with status indicators
 */
export const CsfFileCard = ({
  className,
  error,
  file,
  isExtracting = false,
  onRemove,
  showRemoveButton = 'always',
}: CsfFileCardProps): React.ReactElement => {
  const { t } = useTranslation('invoiceData');

  const shouldShowRemoveButton =
    onRemove && !isExtracting && (showRemoveButton === 'always' || (showRemoveButton === 'on-error' && Boolean(error)));

  return (
    <div className={cn('flex flex-col gap-2 font-sans', className)}>
      <div className="flex items-center gap-2 rounded-lg border border-border bg-muted p-3">
        <FileText className="h-4 w-4 shrink-0 text-primary" strokeWidth={1.5} />
        <span className="flex-1 truncate text-sm text-foreground">{file.name}</span>
        {shouldShowRemoveButton && (
          <button
            aria-label={t('invoiceDataForm.uploadStep.removeFile')}
            className="h-5 w-5 shrink-0 rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
            onClick={onRemove}
            type="button"
          >
            <X className="h-full w-full" />
          </button>
        )}
      </div>

      {isExtracting && (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          {t('invoiceDataForm.uploadStep.extracting')}
        </p>
      )}

      {!isExtracting && error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
};
